import React, { useEffect, useState } from "react";
import { Tag, Tooltip } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getActiveSeasons } from "../../api/Services";
import type { ActiveSeasonItem, ActiveSeasonsResponse } from "../../types/activeSeasons";
import styles from "./ActiveSeasonsNotice.module.scss";

let cached: { day: string; promise: Promise<ActiveSeasonsResponse | null> } | null = null;

const loadActiveSeasons = (): Promise<ActiveSeasonsResponse | null> => {
  const day = new Date().toDateString();
  if (!cached || cached.day !== day) {
    const promise = getActiveSeasons().then((res) => {
      if (!res) cached = null;
      return res as ActiveSeasonsResponse | null;
    });
    cached = { day, promise };
  }
  return cached.promise;
};

const formatMonthDay = (monthDay: string) => {
  const [month, day] = monthDay.split("-");
  return `${day}/${month}`;
};

const seasonRange = (s: ActiveSeasonItem) =>
  `${formatMonthDay(s.startMonthDay)} – ${formatMonthDay(s.endMonthDay)}`;

const ActiveSeasonsNotice: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ActiveSeasonsResponse | null>(null);

  useEffect(() => {
    let active = true;
    void loadActiveSeasons().then((res) => {
      if (active) setData(res);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!data?.filterEnabled) return null;

  return (
    <div className={styles.notice}>
      <CalendarOutlined className={styles.icon} />
      <span className={styles.label}>{t("ActiveSeasons-Label")}</span>
      {data.seasons.length === 0 ? (
        <span className={styles.none}>{t("ActiveSeasons-None")}</span>
      ) : (
        data.seasons.map((s) => (
          <Tooltip key={s.id} title={seasonRange(s)}>
            <Tag className={styles.tag}>{s.name}</Tag>
          </Tooltip>
        ))
      )}
      <span className={styles.hint}>{t("ActiveSeasons-Hint")}</span>
    </div>
  );
};

export default ActiveSeasonsNotice;
